import React, { useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  LinearProgress,
  Fade,
  Alert,
  TextField,
} from '@mui/material';
import {
  Clear as ClearIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  AutoFixHigh as AutoFixIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { TranscriptSegment } from '../../types/voice';
import { useSnackbar } from 'notistack';
import {
  correctVeterinaryTermsAdvanced,
  useTerminology
} from '../../terminology';

interface VoiceTranscriptDisplayProps {
  transcript: string;
  segments: TranscriptSegment[];
  isRecording: boolean;
  isPaused: boolean;
  isProcessing: boolean;
  recordingDuration: number;
  onClear?: () => void;
  onTranscriptChange?: (transcript: string) => void;
  maxHeight?: number | string;
  showSegments?: boolean;
  enableEditMode?: boolean;
  onEditModeChange?: (enabled: boolean) => void;
}

const VoiceTranscriptDisplay: React.FC<VoiceTranscriptDisplayProps> = ({
  transcript,
  segments,
  isRecording,
  isPaused,
  isProcessing,
  recordingDuration,
  onClear,
  onTranscriptChange,
  maxHeight = 200,
  showSegments = true,
  enableEditMode = false,
  onEditModeChange,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { ready: terminologyReady } = useTerminology();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = React.useState(false);
  const [isEditing, setIsEditing] = React.useState(false);
  const [editedTranscript, setEditedTranscript] = React.useState(transcript);
  const [corrections, setCorrections] = React.useState<Array<{ original: string; corrected: string; position: number }>>([]);
  const [showCorrections, setShowCorrections] = React.useState(false);
  const [isCorrectingTerms, setIsCorrectingTerms] = React.useState(false);

  // Auto-scroll to bottom when new content is added
  useEffect(() => {
    if (scrollRef.current && segments.length > 0) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [segments]);

  // Update edited transcript when transcript changes
  useEffect(() => {
    setEditedTranscript(transcript);
  }, [transcript]);

  // Handle enableEditMode prop
  useEffect(() => {
    if (enableEditMode && !isRecording) {
      setIsEditing(true);
      // Reset the flag after enabling edit mode
      if (onEditModeChange) {
        onEditModeChange(false);
      }
    }
  }, [enableEditMode, isRecording, onEditModeChange]);

  const formatDuration = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleCopy = () => {
    const textToCopy = isEditing ? editedTranscript : transcript;
    if (textToCopy) {
      navigator.clipboard.writeText(textToCopy).then(() => {
        setCopied(true);
        enqueueSnackbar('Transcript copied to clipboard', { variant: 'success' });
        setTimeout(() => setCopied(false), 2000);
      });
    }
  };

  const handleStartEdit = () => {
    setIsEditing(true);
    setEditedTranscript(transcript);
  };

  const handleSaveEdit = () => {
    if (onTranscriptChange) {
      onTranscriptChange(editedTranscript);
    }
    setIsEditing(false);
    enqueueSnackbar('Transcript updated', { variant: 'success' });
  };

  const handleCancelEdit = () => {
    setEditedTranscript(transcript);
    setIsEditing(false);
  };

  const handleCorrectTerminology = async () => {
    setIsCorrectingTerms(true);
    try {
      const result = await correctVeterinaryTermsAdvanced(transcript);
      if (result.corrections.length > 0) {
        setEditedTranscript(result.correctedText);
        setCorrections(result.corrections);
        setShowCorrections(true);
        onTranscriptChange?.(result.correctedText);
        enqueueSnackbar(`Applied ${result.corrections.length} veterinary term corrections`, { 
          variant: 'success' 
        });
      } else {
        enqueueSnackbar('No veterinary term corrections needed', { 
          variant: 'info' 
        });
      }
    } catch (error) {
      console.error('[VoiceTranscriptDisplay] Error correcting terms:', error);
      enqueueSnackbar('Failed to correct terminology', { 
        variant: 'error' 
      });
    } finally {
      setIsCorrectingTerms(false);
    }
  };

  const getStatusColor = () => {
    if (isProcessing) return 'info';
    if (isRecording && !isPaused) return 'error';
    if (isPaused) return 'warning';
    return 'default';
  };

  const getStatusText = () => {
    if (isProcessing) return 'Processing...';
    if (isRecording && !isPaused) return 'Recording';
    if (isPaused) return 'Paused';
    return 'Ready';
  };

  return (
    <Paper
      elevation={1}
      sx={{
        p: 2,
        position: 'relative',
        bgcolor: 'background.paper',
        border: 1,
        borderColor: isRecording && !isPaused ? 'error.main' : 'divider',
        transition: 'border-color 0.3s',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Voice Transcript
          </Typography>
          <Chip
            label={getStatusText()}
            size="small"
            color={getStatusColor()}
            sx={{ minWidth: 80 }}
          />
          {isRecording && (
            <Typography variant="caption" color="text.secondary">
              {formatDuration(recordingDuration)}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {isEditing ? (
            <>
              <Tooltip title="Save changes">
                <IconButton
                  size="small"
                  onClick={handleSaveEdit}
                  color="primary"
                >
                  <SaveIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Cancel editing">
                <IconButton
                  size="small"
                  onClick={handleCancelEdit}
                >
                  <CancelIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <>
              <Tooltip title={!terminologyReady ? "Loading terminology..." : "Correct veterinary terms"}>
                <span>
                  <IconButton
                    size="small"
                    onClick={handleCorrectTerminology}
                    disabled={!transcript || isRecording || !terminologyReady || isCorrectingTerms}
                    color={corrections.length > 0 ? "primary" : "default"}
                  >
                    <AutoFixIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Edit transcript">
                <span>
                  <IconButton
                    size="small"
                    onClick={handleStartEdit}
                    disabled={isRecording}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Copy transcript">
                <span>
                  <IconButton
                    size="small"
                    onClick={handleCopy}
                    disabled={!transcript}
                  >
                    {copied ? <CheckCircleIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
              {onClear && (
                <Tooltip title="Clear transcript">
                  <span>
                    <IconButton
                      size="small"
                      onClick={onClear}
                      disabled={!transcript || isRecording}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Processing indicator */}
      {isProcessing && (
        <LinearProgress
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
          }}
        />
      )}

      {/* Transcript content */}
      {isEditing ? (
        <TextField
          multiline
          rows={8}
          fullWidth
          value={editedTranscript}
          onChange={(e) => setEditedTranscript(e.target.value)}
          placeholder="Edit your transcript here..."
          variant="outlined"
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: 'background.paper',
            },
          }}
        />
      ) : (
        <Box
          ref={scrollRef}
          sx={{
            maxHeight,
            overflowY: 'auto',
            p: 1.5,
            bgcolor: 'grey.50',
            borderRadius: 1,
            minHeight: 100,
            '&::-webkit-scrollbar': {
              width: 8,
            },
            '&::-webkit-scrollbar-track': {
              bgcolor: 'grey.200',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: 'grey.400',
              borderRadius: 4,
              '&:hover': {
                bgcolor: 'grey.500',
              },
            },
          }}
        >
          {!transcript && !segments.length ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontStyle: 'italic', textAlign: 'center', mt: 3 }}
            >
              {isRecording && !isPaused
                ? 'Listening... Start speaking'
                : 'Click the microphone to start recording or click the edit button to type directly'}
            </Typography>
          ) : (
            <>
              {/* Show final transcript */}
              {transcript && (
                <Typography
                  variant="body2"
                  sx={{
                    mb: segments.length > 0 ? 2 : 0,
                    lineHeight: 1.6,
                    color: 'text.primary',
                  }}
                >
                  {transcript}
                </Typography>
              )}

              {/* Show current segment being spoken (interim results) */}
              {showSegments && segments.length > 0 && (
                <Fade in>
                  <Box>
                    {segments
                      .filter(seg => !seg.isFinal)
                      .slice(-1)
                      .map(segment => (
                        <Typography
                          key={segment.id}
                          variant="body2"
                          sx={{
                            color: 'text.secondary',
                            fontStyle: 'italic',
                            opacity: 0.8,
                            animation: 'pulse 1.5s infinite',
                            '@keyframes pulse': {
                              '0%': { opacity: 0.6 },
                              '50%': { opacity: 1 },
                              '100%': { opacity: 0.6 },
                            },
                          }}
                        >
                          {segment.text}
                        </Typography>
                      ))}
                  </Box>
                </Fade>
              )}
            </>
          )}
        </Box>
      )}

      {/* Veterinary term corrections */}
      {corrections.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Veterinary Term Corrections ({corrections.length})
            </Typography>
            <Tooltip title={showCorrections ? "Hide corrections" : "Show corrections"}>
              <IconButton
                size="small"
                onClick={() => setShowCorrections(!showCorrections)}
              >
                {showCorrections ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
          
          {showCorrections && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {corrections.map((correction, index) => (
                <Chip
                  key={index}
                  label={`${correction.original} → ${correction.corrected}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* Confidence indicator for low confidence segments */}
      {segments.some(seg => seg.isFinal && seg.confidence < 0.7) && (
        <Alert
          severity="warning"
          sx={{ mt: 1, py: 0.5 }}
        >
          <Typography variant="caption">
            Some parts of the transcript have low confidence. Please review before processing.
          </Typography>
        </Alert>
      )}
    </Paper>
  );
};

export default VoiceTranscriptDisplay;
